// Settings shell — tab-based: Cài đặt cho Shop, Đại lý, và CTV.
// Each tab is a small sub-component in this file for locality.

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useMediaQuery } from '@/hooks/useIsMobile'
import { useViewportMode } from '@/hooks/useViewportMode'
import { Copy, Check, ChevronLeft, ChevronRight, Loader2, ScanLine } from 'lucide-react'
import { patch, get, post, put } from '@/lib/api'
import { formatPhoneVN } from '@/lib/format'
import { getAuthState, getLoginCodeStatus, updateActiveShopName, requestLinkEmail, verifyLinkEmail, unlinkEmail, subscribe } from '@/lib/auth'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { confirm } from '@/components/ui/confirm'
import { cn } from '@/lib/utils'
import { Button, buttonVariants } from '@/components/ui/button'
import { IndexBadge } from '@/components/ui/data-table'
import { describeDevice } from '@shoptrack/common/user-agent'
import { PageHeader } from '@/components/ui/page-header'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FieldRow } from '@/components/settings/FieldRow'
import { WarehouseManager } from '@/components/settings/WarehouseManager'
import { CarriersPanel } from '@/components/settings/CarriersPanel'
import { BankAccountsManager } from '@/components/settings/BankAccounts'
import { ShopReferralCard } from '@/components/settings/ShopReferralCard'
import { PasswordCriteria } from '@/components/ui/PasswordCriteria'
import { MembersTab } from '@/components/settings/members/MembersTab'
import { MyAccessTab } from '@/components/settings/members/MyAccessTab'
import { staffSettingsTabs, useShell } from '@/components/layout/shell-model'
import { PhoneSettingsHub } from '@/components/layout/PhoneSettingsHub'
import { AvatarField } from '@/components/settings/ShopAvatar'
import { roleLine } from '@/components/layout/UnitSwitcher'

type Tab = 'general' | 'members' | 'access' | 'warehouse' | 'carriers' | 'banks' | 'security' | 'notifications'

// Thành viên & vai trò (docs/KE_HOACH_THANH_VIEN_DON_VI.md): chủ đơn vị thấy "Nhân viên"; nhân viên chỉ thấy các mục
// của chính mình (staffSettingsTabs) — mục Ngân hàng, Hãng vận chuyển, Thông tin chung là việc của chủ.
const MEMBERS_TAB: { key: Tab; label: string } = { key: 'members', label: 'Nhân viên' }
const STAFF_TAB_LABEL: Partial<Record<Tab, string>> = {
  access: 'Quyền của tôi',
  warehouse: 'Kho lấy hàng',
  security: 'Bảo mật',
  notifications: 'Thông báo',
}

const TABS: { key: Tab; label: string }[] = [
  { key: 'general', label: 'Thông tin chung' },
  MEMBERS_TAB,
  { key: 'warehouse', label: 'Kho lấy hàng' },
  { key: 'carriers', label: 'Hãng vận chuyển' },
  { key: 'banks', label: 'Ngân hàng' },
  { key: 'security', label: 'Bảo mật' },
  { key: 'notifications', label: 'Thông báo' },
]

export default function SettingsPage() {
  const { tab: pathTab } = useParams<{ tab?: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const tabParam = (searchParams.get('tab') || pathTab) as Tab | null

  const auth = getAuthState()
  const currentShop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const isCtv = currentShop?.tenantType === 'ctv' || currentShop?.role === 'ctv'
  const isAgency = currentShop?.tenantType === 'agency' || currentShop?.role === 'agency'
  const isStaff = currentShop?.role === 'staff'

  // Tuyệt đối không hiển thị tab API & Tích hợp cho cả 3 phân quyền: Đại lý, CTV và Shop
  const tabs: { key: Tab; label: string }[] = isStaff
    ? staffSettingsTabs(currentShop?.staff?.permissions ?? []).map((k) => ({ key: k as Tab, label: STAFF_TAB_LABEL[k as Tab] ?? k }))
    : isCtv
    ? [
        { key: 'general', label: 'Thông tin chung' },
        MEMBERS_TAB,
        { key: 'warehouse', label: 'Kho lấy hàng' },
        { key: 'banks', label: 'Ngân hàng' },
        { key: 'security', label: 'Bảo mật' },
        { key: 'notifications', label: 'Thông báo' },
      ]
    : isAgency
    ? [
        { key: 'general', label: 'Thông tin chung' },
        MEMBERS_TAB,
        { key: 'warehouse', label: 'Kho lấy hàng' },
        { key: 'carriers', label: 'Hãng vận chuyển' },
        { key: 'banks', label: 'Ngân hàng' },
        { key: 'security', label: 'Bảo mật' },
        { key: 'notifications', label: 'Thông báo' },
      ]
    : TABS

  const [tab, setTabState] = useState<Tab>(
    tabParam && tabs.some((t) => t.key === tabParam) ? tabParam : (tabs[0]?.key ?? 'general')
  )

  useEffect(() => {
    const targetTab = (searchParams.get('tab') || pathTab) as Tab | null
    if (targetTab && tabs.some((t) => t.key === targetTab)) {
      setTabState(targetTab)
      // Đường dẫn chỉ đích danh một mục (vd. menu tài khoản → "Bảo mật" khi đang ở danh sách Cài đặt) thì vào luôn mục
      // đó — trang không gắn lại nên `drilled` không tự đổi theo địa chỉ.
      setDrilled(true)
    }
  }, [searchParams, pathTab, isCtv, isAgency, isStaff])

  const setTab = (newTab: Tab) => {
    setTabState(newTab)
    setSearchParams({ tab: newTab }, { replace: true })
  }

  // Trên điện thoại, `/settings` trần mở ra DANH SÁCH mục; chỉ khi đường dẫn
  // chỉ đích danh một mục (hoặc người dùng chạm vào một hàng) mới vào nội dung.
  const [drilled, setDrilled] = useState<boolean>(Boolean(tabParam))
  const openTab = (next: Tab) => {
    setTab(next)
    setDrilled(true)
  }
  const closeTab = () => {
    setDrilled(false)
    if (pathTab) navigate('/settings', { replace: true })
    else setSearchParams({}, { replace: true })
  }

  // Bố cục hai cột cần HAI điều kiện, không phải một:
  //   - đang ở chế độ desktop — iPad Pro nằm ngang rộng 1366px nhưng vẫn là
  //     giao diện mobile, cho nó bố cục desktop thì lệch hẳn với phần còn lại
  //     của app (thanh tab đáy, danh sách dạng thẻ);
  //   - và đủ rộng — dưới 1280px thì hai cột chật.
  // Bản cũ chỉ xét bề rộng nên iPad Pro rơi vào bố cục desktop.
  const roomy = useMediaQuery('(min-width: 1280px)')
  const wide = useViewportMode() === 'desktop' && roomy
  const activeTab = tabs.find((t) => t.key === tab) ?? tabs[0]
  // Khung Glass (điện thoại): danh sách mục kèm cả menu tài khoản cũ — tab "Cài đặt" của thanh đáy
  const shell = useShell()

  return (
    <div className="max-w-5xl">
      {/* ══ Điện thoại: DANH SÁCH mục, rồi mới vào từng mục ══════════════
          Dải tab cuộn ngang cũ rộng 804px (Shop) trong khung
          358px, nên 4–5 trong 7 mục nằm hẳn ngoài màn hình mà không có dấu
          hiệu nào cho biết chúng tồn tại — kể cả "Bảo mật tài khoản". Một
          danh sách dọc cho thấy đủ cả 7 mục ngay từ đầu. ═══════════════ */}
      {!wide ? (
        !drilled && shell ? (
          <PhoneSettingsHub shell={shell} tabs={tabs} onOpen={openTab} />
        ) : !drilled ? (
          <>
            <PageHeader title="Cài đặt" />
            <nav className="mt-4 overflow-hidden rounded-xl bg-raise shadow-card">
              {tabs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => openTab(t.key)}
                  className="motion-state flex min-h-[52px] w-full items-center justify-between gap-3 border-b border-rule px-4 text-left last:border-b-0 active:bg-paperSoft"
                >
                  <span className="text-sm text-ink">{t.label}</span>
                  <ChevronRight className="size-4 shrink-0 text-muted" />
                </button>
              ))}
              </nav>
          </>
        ) : (
          <>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={closeTab}
                className="motion-state -ml-2 flex min-h-11 items-center gap-1 px-2 text-sm text-accent"
              >
                <ChevronLeft className="size-4" />
                Cài đặt
              </button>
            </div>
            <h1 className="mt-1 mb-4">{activeTab?.label}</h1>
            <TabPanel tab={tab} setTab={setTab} />
          </>
        )
      ) : (
        <>
          <PageHeader title="Cài đặt" className="mb-6" />
          <div className="grid grid-cols-[184px_minmax(0,1fr)] gap-8">
            <nav className="flex flex-col gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'motion-state rounded-md px-3 py-2 text-left text-sm',
                  tab === t.key
                    ? 'bg-accent/10 font-medium text-accent'
                    : 'text-inkSoft hover:bg-paperSoft hover:text-ink',
                )}
              >
                {t.label}
              </button>
            ))}
          </nav>

            <div className="min-w-0">
              <TabPanel tab={tab} setTab={setTab} />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** Nội dung của một mục cài đặt — dùng chung cho cả hai bố cục. */
function TabPanel({ tab, setTab }: { tab: Tab; setTab: (t: Tab) => void }) {
  return (
    <>
      {tab === 'general' && <GeneralTab />}
      {tab === 'members' && <MembersTab />}
      {tab === 'access' && <MyAccessTab />}
      {tab === 'warehouse' && <WarehouseTab />}
      {tab === 'carriers' && <CarriersPanel onGoToWarehouse={() => setTab('warehouse')} />}
      {tab === 'banks' && <BanksTab />}
      {tab === 'security' && <SecurityTab />}
      {tab === 'notifications' && <NotificationsTab />}
    </>
  )
}

// ─── General Tab (Thông tin Cửa hàng / Đại lý / CTV) ───────────

function GeneralTab() {
  const auth = getAuthState()
  const currentShop = auth.shops.find((s) => s.shopId === auth.activeShopId)
  const isCtv = currentShop?.tenantType === 'ctv' || currentShop?.role === 'ctv'
  // Shop giới thiệu shop ngang hàng (ShopReferralCard) — Đại lý / CTV giữ link cấp dưới như cũ
  const isShop = currentShop?.tenantType === 'shop' && !isCtv
  const isAgency = currentShop?.tenantType === 'agency' && !isCtv
  // Link mời mở Shop: mã giới thiệu (không phải mã đơn vị) + địa chỉ trang đang mở — cùng nguồn
  // GET /network/referral với trang Mạng lưới & Tổng quan CTV. Chưa chọn bảng Shop cho link = tắt.
  const { data: referral, isLoading: loadingReferral } = useQuery({
    queryKey: ['network-referral'],
    queryFn: () => get<{ code: string | null; enabled: boolean }>('/network/referral'),
    enabled: isCtv,
  })
  const referralLink = referral?.enabled && referral.code ? `${window.location.origin}/signup?ref=${encodeURIComponent(referral.code)}` : null
  const [copied, setCopied] = useState(false)
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['shop-settings', auth.activeShopId],
    queryFn: () => get<{ shop: any; senderName: string | null; displayName: string; avatarUrl?: string | null }>('/settings/shop'),
  })

  const [name, setName] = useState('')

  useEffect(() => {
    if (data?.displayName) {
      setName(data.displayName)
    } else if (currentShop?.name) {
      setName(currentShop.name)
    }
  }, [data, currentShop])

  const save = useMutation({
    mutationFn: (newName: string) => patch<{ ok: boolean; displayName: string }>('/settings/shop', { name: newName }),
    onSuccess: (res) => {
      const savedName = res.displayName || name.trim()
      updateActiveShopName(savedName)
      refetch()
      toast.success('Đã cập nhật tên')
    },
    onError: (e: any) => toast.error(e?.message || 'Không thể cập nhật tên'),
  })

  return (
    <div className="space-y-6">
      <AvatarField name={data?.displayName || currentShop?.name || ''} src={data?.avatarUrl} />
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (!name.trim()) return
          save.mutate(name.trim())
        }}
        className="rounded-xl p-6 bg-raise space-y-4 shadow-card"
      >
        <div>
          <h3 className="font-medium text-base text-ink">
            {isCtv ? 'Thông tin CTV' : isAgency ? 'Thông tin Đại lý' : 'Thông tin Shop'}
          </h3>
        </div>

        <div className="pt-2 max-w-lg space-y-4">
          <FieldRow label={isCtv ? 'Tên CTV' : isAgency ? 'Tên Đại lý' : 'Tên Shop'}>
            <Input
              required
              placeholder={isCtv ? 'VD: Nguyễn Văn A (CTV Sài Gòn)' : 'VD: Tổng Kho Thời Trang Sài Gòn'}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLoading || save.isPending}
            />
          </FieldRow>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 text-xs text-inkSoft">
            <div className="p-3 rounded-md bg-paperSoft border border-rule/60">
              <span className="text-muted block text-xs font-medium mb-1">
                {isCtv ? 'Mã CTV' : 'Mã định danh'}
              </span>
              <span className="font-mono font-semibold">{currentShop?.shopId || '—'}</span>
            </div>
            <div className="p-3 rounded-md bg-paperSoft border border-rule/60">
              <span className="text-muted block text-xs font-medium mb-1">Cấp bậc tài khoản</span>
              <span className="font-semibold text-accent">
                {currentShop ? roleLine(currentShop) : '—'}
              </span>
            </div>
          </div>

          {isCtv && (
            <div className="p-4 rounded-lg bg-accent/50 border border-accent space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-accent ">
                  Link mời mở Shop
                </span>
                {referralLink && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      navigator.clipboard.writeText(referralLink).then(
                        () => {
                          setCopied(true)
                          toast.success('Đã sao chép link mời')
                          setTimeout(() => setCopied(false), 2000)
                        },
                        () => toast.error('Trình duyệt chặn sao chép.'),
                      )
                    }}
                    className="h-7 px-2 text-xs text-accent hover:text-accent hover:bg-accent/12 dark:hover:bg-accent/40"
                  >
                    {copied ? <Check className="size-3 mr-1 text-ok" /> : <Copy className="size-3 mr-1" />}
                    {copied ? 'Đã chép' : 'Sao chép'}
                  </Button>
                )}
              </div>
              {referralLink ? (
                <p className="text-xs font-mono text-inkSoft truncate select-all bg-paperSoft px-2.5 py-1.5 rounded border border-rule" title={referralLink}>
                  {referralLink}
                </p>
              ) : (
                <p className="text-xs text-inkSoft">
                  {loadingReferral ? 'Đang tải…' : 'Link đang tắt'}
                </p>
              )}
              {referral?.code && (
                <p className="text-xs text-muted">
                  Mã giới thiệu: <span className="font-mono text-ink">{referral.code}</span>
                </p>
              )}
            </div>
          )}
        </div>

        <div className="pt-2">
          <Button type="submit" disabled={save.isPending || !name.trim() || name === data?.displayName}>
            {save.isPending ? 'Đang lưu...' : 'Lưu'}
          </Button>
        </div>
      </form>

      {isShop && <ShopReferralCard />}
    </div>
  )
}

// ─── Warehouse Tab — nhiều kho lấy hàng (GĐ3) ─────────────────
// Dùng chung với màn "Hoàn tất thông tin tài khoản" — xem components/settings/WarehouseManager.

function WarehouseTab() {
  return <WarehouseManager />
}

// ─── Security tab ───────────────────────────────────────

interface SessionRow {
  id: string
  ipFirst: string | null
  ipLast: string | null
  userAgent: string | null
  createdAt: string
  lastUsedAt: string
  expiresAt: string
  isCurrent: boolean
}

/** Số phiên hiện sẵn; phần còn lại nằm sau một cú bấm. */
const SESSION_PREVIEW = 5

export function SecurityTab() {
  const [auth, setAuth] = useState(getAuthState())
  const phone = useViewportMode() === 'mobile'
  useEffect(() => {
    const unsub = subscribe(setAuth)
    return () => {
      unsub()
    }
  }, [])

  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: () => get<{ sessions: SessionRow[] }>('/auth/sessions'),
  })
  // Mã xác nhận qua email khi đăng nhập — hệ thống bật / tắt (Super Admin), gửi về email liên kết
  const loginCode = useQuery({ queryKey: ['login-code-status'], queryFn: getLoginCodeStatus })

  // Link Email modal state
  const [showLinkEmailModal, setShowLinkEmailModal] = useState(false)
  const [linkEmailStep, setLinkEmailStep] = useState<'input' | 'verify'>('input')
  const [inputEmail, setInputEmail] = useState('')
  const [inputOtp, setInputOtp] = useState('')
  const [busyEmail, setBusyEmail] = useState(false)

  // Change password form
  const [currPass, setCurrPass] = useState('')
  const [newPass, setNewPass] = useState('')
  const [confirmPass, setConfirmPass] = useState('')
  const [isNewPassValid, setIsNewPassValid] = useState(false)
  const [busyPass, setBusyPass] = useState(false)
  const [showAllSessions, setShowAllSessions] = useState(false)

  const revokeM = useMutation({
    mutationFn: (id: string) => post(`/auth/sessions/${id}/revoke`),
    onSuccess: () => { sessions.refetch(); toast.success('Đã đăng xuất phiên đó') },
    onError: (e: any) => toast.error(e?.message ?? 'Không đăng xuất được'),
  })
  // Danh sách phiên tăng vô hạn theo mỗi lần đăng nhập — render hết khiến tab
  // Bảo mật dài tới 8 màn hình. Mặc định chỉ hiện vài phiên gần nhất.
  const allSessions = sessions.data?.sessions ?? []
  const sessionCount = allSessions.length
  const visibleSessions = showAllSessions ? allSessions : allSessions.slice(0, SESSION_PREVIEW)

  const revokeAllM = useMutation({
    mutationFn: () => post<{ revoked: number }>('/auth/sessions/revoke-others'),
    onSuccess: (r) => { sessions.refetch(); toast.success(`Đã đăng xuất ${r.revoked} phiên khác`) },
    onError: (e: any) => toast.error(e?.message ?? 'Không đăng xuất được'),
  })

  async function onRevoke(id: string) {
    const ok = await confirm({
      title: 'Đăng xuất phiên này?',
      description: 'Thiết bị đó sẽ phải đăng nhập lại từ đầu.',
      confirmLabel: 'Đăng xuất',
    })
    if (ok) revokeM.mutate(id)
  }
  async function onRevokeAll() {
    const ok = await confirm({
      title: 'Đăng xuất tất cả thiết bị khác?',
      description: 'Các máy khác phải đăng nhập lại.',
      tone: 'danger',
      confirmLabel: 'Đăng xuất tất cả khác',
    })
    if (ok) revokeAllM.mutate()
  }

  async function handleSendEmailOtp() {
    if (!inputEmail.includes('@')) {
      toast.error('Email không hợp lệ')
      return
    }
    setBusyEmail(true)
    try {
      await requestLinkEmail(inputEmail)
      toast.success(`Đã gửi mã xác nhận đến ${inputEmail}`)
      setLinkEmailStep('verify')
    } catch (e: any) {
      toast.error(e?.message ?? 'Không gửi được email xác nhận')
    } finally {
      setBusyEmail(false)
    }
  }

  async function handleVerifyEmailOtp() {
    if (inputOtp.length !== 6) {
      toast.error('Vui lòng nhập đủ 6 chữ số mã xác nhận')
      return
    }
    setBusyEmail(true)
    try {
      await verifyLinkEmail(inputEmail, inputOtp)
      toast.success('Đã liên kết email thành công!')
      setShowLinkEmailModal(false)
      setInputEmail('')
      setInputOtp('')
    } catch (e: any) {
      toast.error(e?.message ?? 'Mã xác nhận không đúng hoặc đã hết hạn')
    } finally {
      setBusyEmail(false)
    }
  }

  async function handleUnlinkEmail() {
    const ok = await confirm({
      title: 'Hủy liên kết Email?',
      description: 'Sau khi hủy, bạn cần khai email mới mới tiếp tục tạo đơn được.',
      tone: 'danger',
      confirmLabel: 'Hủy liên kết',
      cancelLabel: 'Đóng',
    })
    if (!ok) return
    try {
      await unlinkEmail()
      toast.success('Đã hủy liên kết email')
    } catch (e: any) {
      toast.error(e?.message ?? 'Không thể hủy liên kết email')
    }
  }

  async function handleChangePassword(e: React.FormEvent) {
    e.preventDefault()
    if (!currPass || !newPass) return
    if (!isNewPassValid) {
      toast.error('Mật khẩu mới chưa thỏa mãn đủ 5 điều kiện bảo mật')
      return
    }
    if (newPass !== confirmPass) {
      toast.error('Mật khẩu xác nhận không khớp')
      return
    }
    setBusyPass(true)
    try {
      await post('/auth/password/change', { currentPassword: currPass, newPassword: newPass })
      toast.success('Đổi mật khẩu thành công. Các thiết bị khác đã được đăng xuất.')
      setCurrPass('')
      setNewPass('')
      setConfirmPass('')
      sessions.refetch()
    } catch (e: any) {
      toast.error(e?.message ?? 'Không đổi được mật khẩu')
    } finally {
      setBusyPass(false)
    }
  }

  return (
    <div className="space-y-8">

      {/* ── Account Identifiers (Phone & Email Link) ── */}
      <div className="rounded-xl bg-raise p-5 space-y-4 shadow-card">
        <div className="flex items-center gap-2">
          <h3 className="text-lg text-ink">Thông tin đăng nhập & liên kết</h3>
        </div>

        <div className="grid gap-4 divide-y divide-rule sm:grid-cols-2 sm:gap-6 sm:divide-y-0 sm:divide-x">
          {/* Phone Primary Identifier */}
          <div className="space-y-2 pt-4 first:pt-0 sm:pl-0 sm:pt-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-medium text-inkSoft">
                <span>Số điện thoại đăng nhập</span>
              </div>
            </div>
            <p className="font-mono text-base font-semibold text-ink">
              {auth.phone ? formatPhoneVN(auth.phone) : 'Chưa cập nhật'}
            </p>
          </div>

          {/* Email Linked Account */}
          <div className="space-y-2 pt-4 first:pt-0 sm:pl-6 sm:pt-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-xs font-medium text-inkSoft">
                <span>Email liên kết</span>
              </div>
              {auth.email ? (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-ok/12 text-ok">
                  Đã xác minh
                </span>
              ) : (
                <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-warn/12 text-warn">
                  Chưa liên kết
                </span>
              )}
            </div>
            <p className="font-mono text-base font-semibold text-ink truncate">
              {auth.email || 'Chưa liên kết email'}
            </p>
            <div className="flex items-center justify-between pt-1">
              {auth.email ? (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 px-2.5"
                    onClick={() => { setShowLinkEmailModal(true); setLinkEmailStep('input'); setInputEmail(auth.email || ''); setInputOtp('') }}
                  >
                    Đổi email
                  </Button>
                  {auth.phone && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2 text-danger hover:bg-danger/10"
                      onClick={handleUnlinkEmail}
                    >
                      Hủy liên kết
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  size="sm"
                  className="text-xs h-7 px-3"
                  onClick={() => { setShowLinkEmailModal(true); setLinkEmailStep('input'); setInputEmail(''); setInputOtp('') }}
                >
                  Liên kết
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Mã xác nhận khi đăng nhập (email) ── */}
      <div className="rounded-xl bg-raise p-5 space-y-2 shadow-card">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <p className="font-medium text-ink">Mã xác nhận khi đăng nhập</p>
          {loginCode.data && (
            <span className={cn(
              'text-xs px-2 py-0.5 rounded-full font-medium',
              loginCode.data.active ? 'bg-ok/12 text-ok' : loginCode.data.required ? 'bg-warn/12 text-warn' : 'bg-muted/20 text-muted',
            )}>
              {loginCode.data.active ? 'Đang bật' : loginCode.data.required ? 'Chưa có email' : 'Đang tắt'}
            </span>
          )}
        </div>
      </div>

      {/* ── Change Password Section ── */}
      <div className="rounded-xl bg-raise p-5 space-y-4 shadow-card">
        <div className="flex items-center gap-2">
          <h3 className="text-lg text-ink">Đổi mật khẩu</h3>
        </div>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
          <div>
            <Label htmlFor="currPass" className="text-xs">Mật khẩu hiện tại</Label>
            <Input
              id="currPass"
              type="password"
              placeholder="••••••••••"
              value={currPass}
              onChange={(e) => setCurrPass(e.target.value)}
              className="mt-1 text-base sm:text-sm"
              required
            />
          </div>
          <div>
            <Label htmlFor="newPass" className="text-xs">Mật khẩu mới</Label>
            <Input
              id="newPass"
              type="password"
              placeholder="••••••••••"
              value={newPass}
              onChange={(e) => setNewPass(e.target.value)}
              className="mt-1 text-base sm:text-sm"
              required
            />
            {newPass.length > 0 && (
              <PasswordCriteria
                password={newPass}
                onValidChange={setIsNewPassValid}
                className="mt-2"
              />
            )}
          </div>
          <div>
            <Label htmlFor="confirmPass" className="text-xs">Nhập lại mật khẩu mới</Label>
            <Input
              id="confirmPass"
              type="password"
              placeholder="••••••••••"
              value={confirmPass}
              onChange={(e) => setConfirmPass(e.target.value)}
              className="mt-1 text-base sm:text-sm"
              required
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={busyPass || !currPass || !newPass || !isNewPassValid || newPass !== confirmPass}
          >
            {busyPass ? 'Đang cập nhật…' : 'Đổi mật khẩu'}
          </Button>
        </form>
      </div>

      {/* ── Active Sessions Section ── */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-ink">Thiết bị đang đăng nhập</h3>
            <p className="mt-0.5 text-xs text-muted">{sessionCount} phiên đang hoạt động</p>
          </div>
          {sessionCount > 1 && (
            <Button variant="outline" size="sm" onClick={onRevokeAll}>
              Đăng xuất tất cả khác
            </Button>
          )}
        </div>
        {/* Đăng nhập máy khác bằng mã QR — chỉ giao diện điện thoại (docs/KE_HOACH_DANG_NHAP_QR.md) */}
        {phone && (
          <Link to="/qr" className={cn(buttonVariants({ variant: 'outline' }), 'mb-3 w-full')}>
            <ScanLine aria-hidden />
            Quét mã đăng nhập
          </Link>
        )}
        <ul className="border border-rule rounded-lg bg-raise divide-y divide-rule">
          {visibleSessions.map((s, i) => (
            <li key={s.id} className="p-4 flex items-center gap-3">
              <div className={cn(
                'size-2 rounded-full shrink-0',
                s.isCurrent ? 'bg-ok' : 'bg-inkSoft/40',
              )} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <IndexBadge n={i + 1} />
                  <p className="text-sm text-ink">
                    {describeDevice(s.userAgent) || 'Trình duyệt không xác định'}
                  </p>
                  {s.isCurrent && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-ok/12 text-ok">
                      Thiết bị này
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted mt-0.5 tabular-nums">
                  IP {s.ipLast ?? s.ipFirst ?? '—'} · hoạt động {new Date(s.lastUsedAt).toLocaleString('vi-VN')}
                </p>
              </div>
              {!s.isCurrent && (
                <Button variant="ghost" size="sm" onClick={() => onRevoke(s.id)}>
                  Đăng xuất
                </Button>
              )}
            </li>
          ))}
          {sessions.data && sessionCount === 0 && (
            <li className="p-6 text-center text-sm text-muted">Không có phiên nào</li>
          )}
        </ul>
        {sessionCount > SESSION_PREVIEW && (
          <button
            type="button"
            onClick={() => setShowAllSessions((v) => !v)}
            className="motion-state mt-2 text-xs text-accent hover:underline"
          >
            {showAllSessions
              ? 'Thu gọn'
              : `Xem tất cả ${sessionCount} phiên`}
          </button>
        )}
      </div>

      {/* ── Link Email Modal ── */}
      <Dialog open={showLinkEmailModal} onOpenChange={setShowLinkEmailModal}>
        <DialogContent className="max-w-md" aria-describedby={linkEmailStep === 'input' ? undefined : 'link-email-desc'}>
          <DialogHeader>
            <DialogTitle>{auth.email ? 'Đổi email' : 'Liên kết email'}</DialogTitle>
            {linkEmailStep === 'verify' && (
              <DialogDescription id="link-email-desc">
                Đã gửi mã đến {inputEmail}. Mã có hiệu lực trong 15 phút.
              </DialogDescription>
            )}
          </DialogHeader>

          {linkEmailStep === 'input' ? (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="linkEmailInput" className="text-xs">Địa chỉ email</Label>
                <div className="relative">
                  <Input
                    id="linkEmailInput"
                    type="email"
                    placeholder="chushop@gmail.com"
                    value={inputEmail}
                    onChange={(e) => setInputEmail(e.target.value)}
                    className="pl-10"
                    autoFocus
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="linkOtpInput" className="text-xs">Mã xác nhận (6 chữ số)</Label>
                <Input
                  id="linkOtpInput"
                  type="text"
                  maxLength={6}
                  placeholder="123456"
                  value={inputOtp}
                  onChange={(e) => setInputOtp(e.target.value.replace(/\D/g, ''))}
                  className="font-mono text-center tracking-widest text-lg font-bold"
                  autoFocus
                />
              </div>
              <div className="flex justify-between items-center text-xs">
                <button
                  type="button"
                  onClick={() => setLinkEmailStep('input')}
                  className="text-accent hover:underline"
                >
                  Đổi email khác
                </button>
                <button
                  type="button"
                  onClick={handleSendEmailOtp}
                  disabled={busyEmail}
                  className="text-muted hover:text-ink"
                >
                  Gửi lại mã
                </button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowLinkEmailModal(false)}>
              Hủy
            </Button>
            {linkEmailStep === 'input' ? (
              <Button
                size="sm"
                disabled={busyEmail || !inputEmail.includes('@')}
                onClick={handleSendEmailOtp}
              >
                {busyEmail ? 'Đang gửi mã…' : 'Gửi mã xác nhận'}
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={busyEmail || inputOtp.length !== 6}
                onClick={handleVerifyEmailOtp}
              >
                {busyEmail ? 'Đang xác minh…' : 'Xác nhận liên kết'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ─── Banks tab ─────────────────────────────────────────
// Dùng chung với màn "Hoàn tất thông tin tài khoản" — xem components/settings/BankAccounts.

function BanksTab() {
  return <BankAccountsManager />
}

// ─── Notifications Tab (Cài đặt thông báo đa kênh) ───────────────

function NotificationsTab() {
  const queryClient = useQueryClient()
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )

  const { data, isLoading } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => get<{ preferences: any }>('/notifications/preferences'),
  })

  const pref = data?.preferences || {}

  const updateMutation = useMutation({
    mutationFn: (changes: Record<string, boolean>) => put('/notifications/preferences', changes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] })
      toast.success('Đã lưu')
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Không thể lưu cài đặt')
    },
  })

  const testMutation = useMutation({
    mutationFn: () =>
      post('/notifications/test', {
        title: 'Kiểm tra thông báo ShopTrack',
        message: 'Hệ thống thông báo ShopTrack đang hoạt động bình thường trên tài khoản của bạn!',
        sendEmail: true,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
      toast.success('Đã gửi thông báo thử')
    },
    onError: (err: any) => {
      toast.error(err?.message || 'Không thể gửi thông báo thử nghiệm')
    },
  })

  const handleToggle = (key: string, currentValue: boolean) => {
    updateMutation.mutate({ [key]: !currentValue })
  }

  const handleRequestPush = async () => {
    if (typeof Notification === 'undefined') {
      toast.error('Trình duyệt không hỗ trợ thông báo')
      return
    }
    try {
      const perm = await Notification.requestPermission()
      setBrowserPermission(perm)
      if (perm === 'granted') {
        toast.success('Đã bật thông báo')
      } else if (perm === 'denied') {
        toast.error('Quyền thông báo bị chặn')
      }
    } catch {
      toast.error('Lỗi khi yêu cầu cấp quyền thông báo')
    }
  }

  if (isLoading) {
    return (
      <div className="py-12 flex flex-col items-center justify-center text-muted gap-2">
        <Loader2 className="size-5 animate-spin text-accent" />
        <span className="text-xs">Đang tải…</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-rule pb-4">
        <div>
          <h2 className="text-base font-semibold text-ink">Cài đặt thông báo</h2>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => testMutation.mutate()}
          disabled={testMutation.isPending}
          className="h-11 desktop:h-9 text-xs gap-1.5 self-start sm:self-auto"
        >
          <span>{testMutation.isPending ? 'Đang gửi…' : 'Gửi thử'}</span>
        </Button>
      </div>

      {/* Web Push Notification Box */}
      <div className="bg-raise rounded-xl p-5 space-y-4 shadow-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="size-9 rounded-lg bg-accent/10 text-accent flex items-center justify-center shrink-0 mt-0.5">
            </div>
            <div>
              <h3 className="text-sm font-semibold text-ink">Thông báo trên trình duyệt</h3>
              <div className="mt-2 flex items-center gap-2">
                <span
                  className={cn(
                    'text-xs font-semibold px-2 py-0.5 rounded-full border',
                    browserPermission === 'granted'
                      ? 'bg-ok/10 text-ok border-ok '
                      : browserPermission === 'denied'
                      ? 'bg-danger/10 text-danger border-danger '
                      : 'bg-paperSoft text-muted border-rule'
                  )}
                >
                  {browserPermission === 'granted'
                    ? '● Đang bật'
                    : browserPermission === 'denied'
                    ? '● Bị chặn'
                    : '● Chưa kích hoạt'}
                </span>
              </div>
            </div>
          </div>

          {browserPermission !== 'granted' && (
            <Button
              size="sm"
              onClick={handleRequestPush}
              className="h-11 desktop:h-9 px-3 text-xs bg-accent hover:bg-accent/90 text-onAccent font-medium shrink-0"
            >
              Bật thông báo
            </Button>
          )}
        </div>
      </div>

      {/* Email Notifications Settings */}
      <div className="bg-raise rounded-xl p-5 space-y-4 shadow-card">
        <div className="flex items-center gap-2.5 pb-2 border-b border-rule">
          <h3 className="text-sm font-semibold text-ink">Thông báo qua email</h3>
        </div>

        <div className="divide-y divide-rule/60">
          <label className="flex items-center justify-between py-3 cursor-pointer group">
            <div className="pr-4">
              <p className="text-sm font-medium text-ink group-hover:text-accent transition-colors">
                Cảnh báo giao hàng thất bại
              </p>
            </div>
            <input
              type="checkbox"
              checked={pref.emailDeliveryFailed !== false}
              onChange={() => handleToggle('emailDeliveryFailed', pref.emailDeliveryFailed !== false)}
              className="size-4 accent-accent cursor-pointer rounded"
            />
          </label>

          <label className="flex items-center justify-between py-3 cursor-pointer group">
            <div className="pr-4">
              <p className="text-sm font-medium text-ink group-hover:text-accent transition-colors">
                Đối soát & chiết khấu
              </p>
            </div>
            <input
              type="checkbox"
              checked={pref.emailReconciliation !== false}
              onChange={() => handleToggle('emailReconciliation', pref.emailReconciliation !== false)}
              className="size-4 accent-accent cursor-pointer rounded"
            />
          </label>

          <label className="flex items-center justify-between py-3 cursor-pointer group">
            <div className="pr-4">
              <p className="text-sm font-medium text-ink group-hover:text-accent transition-colors">
                Đơn hàng mới
              </p>
            </div>
            <input
              type="checkbox"
              checked={pref.emailOrders !== false}
              onChange={() => handleToggle('emailOrders', pref.emailOrders !== false)}
              className="size-4 accent-accent cursor-pointer rounded"
            />
          </label>

          <label className="flex items-center justify-between py-3 cursor-pointer group">
            <div className="pr-4">
              <p className="text-sm font-medium text-ink group-hover:text-accent transition-colors">
                Chính sách & hệ thống
              </p>
            </div>
            <input
              type="checkbox"
              checked={pref.emailSystem !== false}
              onChange={() => handleToggle('emailSystem', pref.emailSystem !== false)}
              className="size-4 accent-accent cursor-pointer rounded"
            />
          </label>
        </div>
      </div>
    </div>
  )
}

